import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  Typography,
  Badge,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import BlockIcon from "@mui/icons-material/Block";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAdminConversations,
  updateConversationEnabled,
  setActiveConversation,
} from "../../features/messaging/messagingSlice";
import { enableDMApi, disableDMApi } from "../../api/messagingApi";
import UserAvatar from "./UserAvatar";

const userLabel = (c) => {
  const name = c.userDisplayName || c.userFullName || c.userName || c.userEmail || "User";
  return name;
};

const ConversationList = ({ activeId, onSelect, onlineUserIds = new Set() }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = React.useState("");
  const [tab, setTab] = React.useState(0); // 0=All, 1=Active, 2=Disabled
  const [refreshing, setRefreshing] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState(null);
  const conversations = useSelector((state) => state.messaging.conversations);
  const totalUnread = useSelector((state) => state.messaging.totalUnread);

  const disabledCount = conversations.filter((c) => !c.isEnabled).length;

  const handleRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchAdminConversations());
    setTimeout(() => setRefreshing(false), 400);
  };

  const handleToggle = async (e, conv) => {
    e.stopPropagation();
    if (togglingId) return;
    setTogglingId(conv._id);
    const next = !conv.isEnabled;
    dispatch(updateConversationEnabled({ conversationId: conv._id, isEnabled: next }));
    try {
      if (next) {
        const res = await enableDMApi(conv.userId);
        // Refresh list then navigate straight to the conversation
        await dispatch(fetchAdminConversations());
        const targetId = res?.conversation?._id || conv._id;
        dispatch(setActiveConversation(targetId));
      } else {
        await disableDMApi(conv.userId);
      }
    } catch (err) {
      dispatch(updateConversationEnabled({ conversationId: conv._id, isEnabled: !next }));
      console.error("Toggle DM error:", err);
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = conversations.filter((c) => {
    if (tab === 1 && !c.isEnabled) return false;
    if (tab === 2 && c.isEnabled) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      userLabel(c).toLowerCase().includes(q) ||
      (c.userEmail || "").toLowerCase().includes(q)
    );
  });

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60_000) return "now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
    const isToday = d.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === d.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (isYesterday) return "Yesterday";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  return (
    <Box
      sx={{
        width: 340,
        flexShrink: 0,
        borderRight: "1px solid",
        borderColor: "rgba(15,23,42,0.06)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#FFFFFF",
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.75, pt: 2.5, pb: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <Box
            sx={{
              width: 42, height: 42, borderRadius: "12px",
              bgcolor: "#368AD0",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(54,138,208,0.25)",
            }}
          >
            <ForumRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box flex={1}>
            <Typography fontWeight={800} fontSize={18} color="#0F172A" letterSpacing="-0.5px" lineHeight={1.1}>
              Inbox
            </Typography>
            <Typography variant="caption" color="#64748B" fontSize={11.5} fontWeight={500}>
              {conversations.length} {conversations.length === 1 ? "conversation" : "conversations"}
              {totalUnread > 0 && ` · ${totalUnread} unread`}
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={handleRefresh}
              sx={{
                color: "#64748B",
                "&:hover": { bgcolor: "#F1F5F9", color: "#368AD0" },
                transition: "transform 0.4s",
                transform: refreshing ? "rotate(360deg)" : "none",
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              fontSize: 13,
              bgcolor: "#F1F5F9",
              transition: "all 0.15s",
              "& fieldset": { border: "none" },
              "&:hover": { bgcolor: "#E2E8F0" },
              "&.Mui-focused": { bgcolor: "#FFFFFF", boxShadow: "0 0 0 2px rgba(54,138,208,0.2)" },
            },
          }}
        />
      </Box>

      {/* Filter Tabs */}
      <Box sx={{ px: 2.75, borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 36,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              bgcolor: "#368AD0",
            },
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontSize: 12.5,
              fontWeight: 600,
              color: "#64748B",
              minWidth: "auto",
              px: 1.5,
              "&.Mui-selected": { color: "#368AD0", fontWeight: 700 },
            },
          }}
        >
          <Tab label="All" />
          <Tab label="Active" />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                Disabled
                {disabledCount > 0 && (
                  <Box
                    sx={{
                      bgcolor: "#94A3B8",
                      color: "#fff",
                      fontSize: 9.5,
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.05,
                      borderRadius: "10px",
                      minWidth: 16,
                      lineHeight: 1.4,
                    }}
                  >
                    {disabledCount}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* List */}
      {filtered.length === 0 ? (
        <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={3} gap={1.25}>
          <Box
            sx={{
              width: 64, height: 64, borderRadius: "50%",
              bgcolor: "#F1F5F9",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ChatBubbleOutlineIcon sx={{ color: "#94A3B8", fontSize: 30 }} />
          </Box>
          <Typography fontWeight={700} fontSize={14} color="#475569">
            {search ? "No matches" : tab === 2 ? "Nothing disabled" : tab === 1 ? "No active chats" : "No conversations"}
          </Typography>
          <Typography variant="caption" color="#94A3B8" textAlign="center" fontSize={12} maxWidth={220}>
            {search
              ? "Try different keywords"
              : tab === 2
              ? "You haven't disabled any conversations"
              : tab === 1
              ? "Enable DM on a user from their profile"
              : "Enable DM on a user to start chatting"}
          </Typography>
        </Box>
      ) : (
        <List
          disablePadding
          sx={{
            flex: 1,
            overflowY: "auto",
            py: 1,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#E2E8F0", borderRadius: 3 },
            "&::-webkit-scrollbar-thumb:hover": { bgcolor: "#CBD5E1" },
          }}
        >
          {filtered.map((conv) => {
            const isActive = activeId === conv._id;
            const isPinned = !!conv.isPinnedSuperAdmin;
            const unreadCount = isPinned ? (conv.userUnreadCount || 0) : (conv.adminUnreadCount || 0);
            const hasUnread = conv.isEnabled && unreadCount > 0;
            const name = isPinned ? (conv.adminName || "Three Pillars Method") : userLabel(conv);
            const isDisabled = !conv.isEnabled;
            const isToggling = togglingId === conv._id;

            return (
              <ListItemButton
                key={conv._id}
                selected={isActive}
                onClick={() => onSelect(conv)}
                sx={{
                  py: 1.5,
                  px: 1.75,
                  mx: 1.25,
                  my: 0.35,
                  borderRadius: "14px",
                  position: "relative",
                  transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: "1px solid transparent",
                  opacity: isDisabled ? 0.7 : 1,
                  "&.Mui-selected": {
                    bgcolor: isDisabled ? "#F8FAFC" : "#EFF6FF",
                    borderColor: isDisabled ? "rgba(15,23,42,0.08)" : "rgba(54,138,208,0.2)",
                    boxShadow: isDisabled ? "none" : "0 2px 6px rgba(54,138,208,0.06)",
                    "&:hover": { bgcolor: isDisabled ? "#F1F5F9" : "#DBEAFE" },
                  },
                  "&:hover": { bgcolor: "#F8FAFC", transform: "translateY(-1px)" },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 56 }}>
                  {isPinned ? (
                    <Box
                      sx={{
                        width: 46, height: 46, borderRadius: "50%",
                        bgcolor: "#14142B",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: "0 2px 8px rgba(20,20,43,0.25)",
                        border: "2px solid #368AD0",
                      }}
                    >
                      <Box
                        component="img"
                        src="/images/threepillars.png"
                        alt="Three Pillars Method"
                        sx={{ width: 28, height: 28, objectFit: "contain" }}
                      />
                    </Box>
                  ) : (
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        conv.isEnabled && onlineUserIds.has(conv.userId?.toString()) ? (
                          <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: "#10B981", border: "2px solid #fff" }} />
                        ) : null
                      }
                    >
                      <UserAvatar
                        profilePic={conv.userProfilePic}
                        name={name}
                        size={46}
                        fontSize={15}
                        sx={isDisabled ? { filter: "grayscale(0.7)" } : undefined}
                      />
                    </Badge>
                  )}
                </ListItemAvatar>

                <Box flex={1} overflow="hidden" minWidth={0}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.4}>
                    <Typography
                      fontSize={14}
                      fontWeight={hasUnread ? 800 : 600}
                      color={hasUnread ? "#0F172A" : isDisabled ? "#64748B" : "#1E293B"}
                      noWrap
                      sx={{ maxWidth: 145, letterSpacing: "-0.2px" }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={hasUnread ? "#368AD0" : "#94A3B8"}
                      fontSize={10.5}
                      fontWeight={hasUnread ? 700 : 500}
                      flexShrink={0}
                      sx={{ ml: 1, mt: 0.15 }}
                    >
                      {formatTime(conv.lastMessageAt)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    {isPinned && (
                      <Chip
                        label="Admin"
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9.5,
                          fontWeight: 700,
                          bgcolor: "#1E3A5F",
                          color: "#93C5FD",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    )}
                    {!isPinned && isDisabled && (
                      <Chip
                        icon={<BlockIcon sx={{ fontSize: 11 }} />}
                        label="Disabled"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: "#F1F5F9",
                          color: "#64748B",
                          "& .MuiChip-icon": { color: "#64748B", ml: 0.4 },
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color={hasUnread ? "#475569" : "#94A3B8"}
                      noWrap
                      fontSize={12.5}
                      fontWeight={hasUnread ? 600 : 400}
                      flex={1}
                    >
                      {conv.lastMessage || (isDisabled ? "Re-enable to resume" : "Tap to start chatting")}
                    </Typography>
                    {hasUnread && (
                      <Box
                        sx={{
                          minWidth: 20, height: 20, px: 0.65, borderRadius: "10px",
                          bgcolor: "#EF4444",
                          color: "#fff",
                          fontSize: 10.5, fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 2px 6px rgba(239,68,68,0.4)",
                        }}
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Per-row enable/disable toggle — hidden for pinned super-admin conversation */}
                {!isPinned && <Tooltip
                  title={isToggling ? "..." : conv.isEnabled ? "Disable DM" : "Re-enable DM"}
                  placement="left"
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggle(e, conv)}
                    disabled={isToggling}
                    sx={{
                      ml: 0.5,
                      flexShrink: 0,
                      color: conv.isEnabled ? "#368AD0" : "#94A3B8",
                      "&:hover": {
                        bgcolor: conv.isEnabled ? "#FEE2E2" : "#DBEAFE",
                        color: conv.isEnabled ? "#DC2626" : "#368AD0",
                      },
                    }}
                  >
                    {isToggling ? (
                      <CircularProgress size={18} sx={{ color: "#368AD0" }} />
                    ) : conv.isEnabled ? (
                      <ToggleOnIcon sx={{ fontSize: 26 }} />
                    ) : (
                      <ToggleOffIcon sx={{ fontSize: 26 }} />
                    )}
                  </IconButton>
                </Tooltip>}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ConversationList;
